package diana.soleil.design.iraniancanadienbazzar.feature.chat

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ChatInputBar
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ErrorState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.core.model.ChannelPost
import diana.soleil.design.iraniancanadienbazzar.core.model.formatCount
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_composer_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_empty_desc
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_empty_title
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_error
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_follow
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_following
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_label
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_owner_intro
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_retry
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_send_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_time_days_ago
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_time_hours_ago
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_time_just_now
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_time_minutes_ago
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.channel_time_weeks_ago
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_back_a11y
import kotlin.time.Clock
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

/**
 * A business's community channel (`Route.Channel`): broadcast updates from the owner, newest
 * first. Visitors follow to keep the channel in their inbox; the owner gets the composer.
 */
@Composable
fun ChannelRoute(
    businessId: String,
    onBack: () -> Unit,
    onRequireSignIn: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: ChannelViewModel = koinViewModel(parameters = { parametersOf(businessId) }),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    LaunchedEffect(state.signInRequired) { if (state.signInRequired) { viewModel.consumeGate(); onRequireSignIn() } }
    ChannelScreen(
        state = state,
        onBack = onBack,
        onToggleFollow = viewModel::toggleFollow,
        onPost = viewModel::post,
        onRetry = viewModel::load,
        modifier = modifier,
    )
}

@Composable
fun ChannelScreen(
    state: ChannelUiState,
    onBack: () -> Unit,
    onToggleFollow: () -> Unit,
    onPost: (String) -> Unit,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .background(AppTheme.colors.background)
            .appContentWidth(AppTheme.sizes.contentMaxWidth)
            .fillMaxHeight()
            .imePadding()
            .navigationBarsPadding(),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.primaryContainer)
                .statusBarsPadding()
                .padding(horizontal = AppTheme.spacing.lg, vertical = AppTheme.spacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.inbox_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onPrimary,
            )
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = state.businessName,
                    style = AppTheme.typography.headlineMd,
                    color = AppTheme.colors.onPrimary,
                )
                Text(
                    text = stringResource(Res.string.channel_label),
                    style = AppTheme.typography.labelSm,
                    color = AppTheme.colors.onPrimary,
                )
            }
            if (!state.isOwner && !state.loading) {
                AppButton(
                    text = stringResource(
                        if (state.isFollowing) Res.string.channel_following else Res.string.channel_follow,
                    ),
                    onClick = onToggleFollow,
                    variant = if (state.isFollowing) AppButtonVariant.Secondary else AppButtonVariant.Primary,
                    leadingIcon = if (state.isFollowing) "check" else "notifications",
                )
            }
        }
        when {
            state.loading -> LoadingState()
            state.error -> ErrorState(
                message = stringResource(Res.string.channel_error),
                onRetry = onRetry,
                retryLabel = stringResource(Res.string.channel_retry),
            )
            else -> ChannelContent(state = state, onPost = onPost, modifier = Modifier.weight(1f))
        }
    }
}

@Composable
private fun ChannelContent(
    state: ChannelUiState,
    onPost: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxWidth()) {
        if (state.posts.isEmpty()) {
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.Center) {
                if (state.isOwner) {
                    Text(
                        text = stringResource(Res.string.channel_owner_intro, state.businessName),
                        style = AppTheme.typography.bodyMd,
                        color = AppTheme.colors.onSurfaceVariant,
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = AppTheme.spacing.xl),
                    )
                } else {
                    EmptyState(
                        title = stringResource(Res.string.channel_empty_title),
                        description = stringResource(Res.string.channel_empty_desc, state.businessName),
                    )
                }
            }
        } else {
            LazyColumn(
                modifier = Modifier.weight(1f),
                contentPadding = PaddingValues(
                    start = AppTheme.spacing.lg,
                    end = AppTheme.spacing.lg,
                    top = AppTheme.spacing.md,
                    bottom = AppTheme.spacing.xl,
                ),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                items(state.posts, key = { it.id }) { post -> ChannelPostCard(post) }
            }
        }
        if (state.isOwner) {
            var draft by remember { mutableStateOf("") }
            ChatInputBar(
                value = draft,
                onValueChange = { draft = it },
                onSend = {
                    if (draft.isNotBlank()) {
                        onPost(draft)
                        draft = ""
                    }
                },
                placeholder = stringResource(Res.string.channel_composer_placeholder),
                sendContentDescription = stringResource(Res.string.channel_send_a11y),
                sending = state.posting,
            )
        }
    }
}

@Composable
private fun ChannelPostCard(post: ChannelPost) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(AppTheme.colors.surface, AppTheme.shapes.card)
            .padding(AppTheme.spacing.md),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
    ) {
        Text(
            text = post.text,
            style = AppTheme.typography.bodyMd,
            color = AppTheme.colors.onSurface,
        )
        post.createdAtMillis?.let {
            Text(
                text = channelTimeLabel(it),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSurfaceVariant,
            )
        }
    }
}

/** "2 h ago"-style label (localized buckets; future timestamps clamp to "just now"). */
@Composable
private fun channelTimeLabel(millis: Long): String {
    val minutes = ((Clock.System.now().toEpochMilliseconds() - millis) / 60_000L).coerceAtLeast(0)
    val lang = AppTheme.language
    return when {
        minutes < 1 -> stringResource(Res.string.channel_time_just_now)
        minutes < 60 -> stringResource(Res.string.channel_time_minutes_ago, lang.formatCount(minutes))
        minutes < 60 * 24 -> stringResource(Res.string.channel_time_hours_ago, lang.formatCount(minutes / 60))
        minutes < 60 * 24 * 7 -> stringResource(Res.string.channel_time_days_ago, lang.formatCount(minutes / (60 * 24)))
        else -> stringResource(Res.string.channel_time_weeks_ago, lang.formatCount(minutes / (60 * 24 * 7)))
    }
}
